import { ButtonGhost, Card, Checkbox, Colors, Icon, Text } from '@amelio/ui-library';

import { EVALUATION_TYPE_ICON_NAMES } from '../evaluationTypeIcons';

import { EvaluationType } from '@/constants/cycle-evaluation/CycleEvaluation';
import { useAmelioTranslations } from '@/hooks';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';

interface LockedManagerAssessmentTileProps {
    /** Offers Performance PRO, which the Manager Assessment belongs to. */
    onUnlock: () => void;
}

const TILE_GLYPH_SIZE = 28;

/**
 * The Manager Assessment tile as a free-tier company sees it: what it would add, its two options
 * and its hint, none of them selectable, and the way to unlock it. It is the place the reader meets
 * Performance PRO in this wizard, which is why it stays while the steps downstream simply drop it.
 */
export const LockedManagerAssessmentTile = ({ onUnlock }: LockedManagerAssessmentTileProps) => {
    const { t } = useAmelioTranslations();
    const { getManagerLabel } = useCompanyInformation();
    const managerLabel = getManagerLabel();

    return (
        <Card
            bgColor={'bg-white'}
            borderColor={Colors.BASE_50}
            padding={'lg'}
            borderRadius={'xl'}
        >
            <div
                className={'flex flex-wrap items-start gap-4'}
                data-testid={'evaluation-type-manager-locked'}
            >
                <Icon
                    name={EVALUATION_TYPE_ICON_NAMES[EvaluationType.MANAGER_EVALUATION]}
                    size={TILE_GLYPH_SIZE}
                    color={Colors.BASE_400}
                    ariaHidden={true}
                />

                <div className={'min-w-0 flex-1'}>
                    <Text
                        weight={'bold'}
                        className={'text-[18px] leading-[28px] tracking-tight'}
                    >
                        {t('cycleEvaluationEvaluationElements.types.managerAssessment.label', {
                            managerLabel,
                        })}
                    </Text>

                    <Text
                        color={Colors.STEP_RAIL_MUTED}
                        className={'mt-1 text-[14px] leading-[22.75px]'}
                    >
                        {t(
                            'cycleEvaluationEvaluationElements.types.managerAssessment.description',
                            {
                                managerLabel,
                            },
                        )}
                    </Text>
                </div>

                <ButtonGhost
                    size={'sm'}
                    leftIcon={
                        <Icon
                            name={'security-lock'}
                            ariaHidden={true}
                        />
                    }
                    onClick={onUnlock}
                    dataTestId={'evaluation-type-manager-unlock'}
                >
                    {t('performancePro.unlockAction')}
                </ButtonGhost>
            </div>

            <div
                className={
                    'mt-4 flex flex-wrap gap-x-8 gap-y-3 border-t border-[var(--color-step-rail-track)] pt-4'
                }
            >
                <Checkbox
                    checked={false}
                    disabled={true}
                    label={t('cycleEvaluationEvaluationElements.subOptions.assessCompetencies')}
                    dataTestId={'assess-competencies-checkbox-locked'}
                />

                <Checkbox
                    checked={false}
                    disabled={true}
                    label={t(
                        'cycleEvaluationEvaluationElements.subOptions.assessOrganizationalValues',
                    )}
                    dataTestId={'assess-organizational-values-checkbox-locked'}
                />
            </div>

            <Text
                size={'small'}
                color={Colors.BASE_400}
                className={'mt-3'}
            >
                {t('cycleEvaluationEvaluationElements.template.managerHint', { managerLabel })}
            </Text>
        </Card>
    );
};
